
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import organizationApi from "@/services/api.organizationSettings";
import {
	isValidIconClass,
	titleFromClass,
	searchIcons,
	AssetMarkerSearchResult,
	DEFAULT_CATEGORY_TITLE,
	ICON_CONFIG_MAX_LENGTH,
	ICON_CONFIG_NEARLY_FULL_LENGTH,
	isUnreadableIconConfig,
} from "@/assets/assetMarkerIcons";

interface EditableCategory {
	title: string;
	icons: string[];
	newIcon: string;
	error: string;
}

// Organization-level management of the custom Asset Type marker icon categories, opened as a modal
// from the Asset Types tab.
@Component({})
export default class AssetIconManager extends Vue {
	@Prop({ default: false }) visible: boolean;

	// Without the manage permission the categories are shown but nothing can be changed: no add,
	// edit, delete or save. The server enforces the same thing.
	@Prop({ default: false }) readOnly: boolean;

	@Action getTenantSystemPrefs: (live: boolean) => Promise<void>;
	@Getter("getAssetTypeIconClassesDefault") private defaultIconClasses: string;

	private loading: boolean = false;
	private saving: boolean = false;
	private message: string = "";
	private isError: boolean = false;

	private categories: EditableCategory[] = [];
	private editingIndex: number | null = null;

	private searchTerm: string = "";
	private searchResults: AssetMarkerSearchResult[] = [];
	private searching: boolean = false;
	private searchTimer: number = 0;
	private showAdvanced: boolean = false;

	private deletingIndex: number | null = null;

	// Held outside `categories` until the admin confirms it, so Cancel really does cancel.
	private draftCategory: EditableCategory | null = null;

	private titleTouched: boolean = false;

	// The table falls back to the system default set when the saved value can't be read, so the
	// admin has to be told — otherwise they take the defaults for their own set and the next save
	// quietly overwrites whatever was really stored.
	private unreadableConfig: boolean = false;

	// A save that would strip a removed icon off asset types still using it waits here for
	// confirmation.
	private showOrphanConfirm: boolean = false;
	private pendingOrphans: string[] = [];
	private pendingPayload: string = "";

	@Watch("visible")
	private async onVisibleChanged(isVisible: boolean): Promise<void> {
		if (isVisible) {
			await this.load();
		}
	}

	// The draft when adding, the row itself when editing an existing category.
	private get editingCategory(): EditableCategory | null {
		if (this.draftCategory) {
			return this.draftCategory;
		}
		return this.editingIndex !== null ? this.categories[this.editingIndex] : null;
	}

	private get isAddingCategory(): boolean {
		return this.draftCategory !== null;
	}

	private get editorVisible(): boolean {
		return this.draftCategory !== null || this.editingIndex !== null;
	}

	private get editorTitle(): string {
		return this.isAddingCategory ? "Add Category" : "Edit Category";
	}

	private get deletingCategory(): EditableCategory | null {
		return this.deletingIndex !== null ? this.categories[this.deletingIndex] : null;
	}

	// Every category must be named: an unnamed one can't be told apart in the Asset Type picker,
	// and saving it would lose its icons.
	private get untitledCount(): number {
		return this.categories.filter(c => !c.title.trim()).length;
	}

	private get canCommitCategory(): boolean {
		return !!this.editingCategory && !!this.editingCategory.title.trim();
	}

	private get editingTitleError(): string {
		if (this.canCommitCategory || !this.editingCategory) {
			return "";
		}
		// A new category opens with the field empty, so don't scold before they've typed anything.
		if (this.isAddingCategory && !this.titleTouched) {
			return "";
		}
		return "Enter a name for this category.";
	}

	// Measure the exact string we would send rather than estimating, so the admin is stopped before
	// building a set the server won't keep (see ICON_CONFIG_MAX_LENGTH).
	private get payloadLength(): number {
		return this.buildPayload().length;
	}

	private get isTooLong(): boolean {
		return this.payloadLength > ICON_CONFIG_MAX_LENGTH;
	}

	private get isNearlyFull(): boolean {
		return !this.isTooLong && this.payloadLength > ICON_CONFIG_NEARLY_FULL_LENGTH;
	}

	private async load(): Promise<void> {
		this.loading = true;
		this.message = "";
		this.editingIndex = null;
		this.deletingIndex = null;
		this.draftCategory = null;
		this.unreadableConfig = false;
		try {
			// The default set is what we seed and fall back to, so it has to be loaded first.
			try {
				await this.getTenantSystemPrefs(true);
			} catch (ex) {
				console.error("Could not load the default icon set: " + ex);
			}

			const raw = await organizationApi.retrieveAssetIconConfig();
			this.unreadableConfig = isUnreadableIconConfig(raw);
			const saved = this.parseCategories(raw);
			// If the organization has none of its own, start from an editable copy of the defaults.
			this.categories = saved.length ? saved : this.parseCategories(this.defaultIconClasses);
		} catch (ex) {
			console.error("Unexpected error loading asset icon categories: " + ex);
			this.categories = this.parseCategories(this.defaultIconClasses);
		} finally {
			this.loading = false;
		}
	}

	private parseCategories(raw: string): EditableCategory[] {
		if (!raw) {
			return [];
		}
		try {
			const arr = JSON.parse(raw);
			if (!Array.isArray(arr)) {
				return [];
			}
			return arr
				.map(c => ({
					title: (c && c.category_title) || "",
					icons:
						c && typeof c.fa_icons === "string" && c.fa_icons
							? c.fa_icons.split(",").map(s => s.trim()).filter(Boolean)
							: [],
					newIcon: "",
					error: "",
				}))
				.filter(c => c.title || c.icons.length);
		} catch (ex) {
			console.error("Could not parse icon categories: " + ex);
			return [];
		}
	}

	private iconLabel(cssClass: string): string {
		return titleFromClass(cssClass);
	}

	// --- table actions ---
	private addCategory(): void {
		this.draftCategory = { title: "", icons: [], newIcon: "", error: "" };
		this.editingIndex = null;
		this.titleTouched = false;
		this.resetSearch();
	}

	private editCategory(index: number): void {
		this.draftCategory = null;
		this.editingIndex = index;
		this.titleTouched = false;
		this.resetSearch();
	}

	private confirmDeleteCategory(index: number): void {
		this.deletingIndex = index;
	}

	private cancelDelete(): void {
		this.deletingIndex = null;
	}

	private removeCategory(index: number): void {
		this.categories.splice(index, 1);
		// The splice renumbers everything after `index`, so an open edit has to follow its category.
		if (this.editingIndex !== null) {
			if (this.editingIndex === index) {
				this.editingIndex = null;
			} else if (this.editingIndex > index) {
				this.editingIndex -= 1;
			}
		}
		this.deletingIndex = null;
	}

	// Every way of dismissing the editor comes through here; only Done ("ok") commits a draft, so
	// Cancel, Esc, the X and the backdrop all discard it.
	private onEditorHide(event?: { trigger?: string }): void {
		if (this.draftCategory && event && event.trigger === "ok") {
			this.categories.push(this.draftCategory);
		}
		this.draftCategory = null;
		this.editingIndex = null;
		this.titleTouched = false;
		this.resetSearch();
	}

	private onTitleInput(): void {
		this.titleTouched = true;
	}

	// --- icon search (edit sub-modal) ---
	private resetSearch(): void {
		this.searchTerm = "";
		this.searchResults = [];
		this.searching = false;
		this.showAdvanced = false;
	}

	private onSearchInput(): void {
		if (this.searchTimer) {
			clearTimeout(this.searchTimer);
		}
		this.searchTimer = window.setTimeout(() => this.runSearch(), 250);
	}

	private async runSearch(): Promise<void> {
		this.searching = true;
		try {
			this.searchResults = await searchIcons(this.searchTerm);
		} catch (ex) {
			console.error("Icon search failed: " + ex);
			this.searchResults = [];
		} finally {
			this.searching = false;
		}
	}

	private isIconInCategory(cssClass: string): boolean {
		return !!this.editingCategory && this.editingCategory.icons.indexOf(cssClass) !== -1;
	}

	private addSearchResult(result: AssetMarkerSearchResult): void {
		if (this.editingCategory && !this.isIconInCategory(result.cssClass)) {
			this.editingCategory.icons.push(result.cssClass);
		}
	}

	// --- icon editing (edit sub-modal) ---
	private canAddIcon(category: EditableCategory): boolean {
		const icon = category.newIcon.trim();
		return isValidIconClass(icon) && category.icons.indexOf(icon) === -1;
	}

	private iconHint(category: EditableCategory): string {
		const icon = category.newIcon.trim();
		if (icon && !isValidIconClass(icon)) {
			return `Not a valid Font Awesome class — use e.g. "fas fa-user-shield".`;
		}
		return category.error;
	}

	private addIcon(category: EditableCategory): void {
		const icon = category.newIcon.trim();
		if (!icon) {
			category.error = "";
			return;
		}
		if (!isValidIconClass(icon)) {
			category.error = `"${icon}" isn't a valid Font Awesome class — use e.g. "fas fa-user-shield".`;
			return;
		}
		if (category.icons.indexOf(icon) !== -1) {
			category.error = "That icon is already in this category.";
			return;
		}
		category.icons.push(icon);
		category.newIcon = "";
		category.error = "";
	}

	private removeIcon(category: EditableCategory, index: number): void {
		category.icons.splice(index, 1);
	}

	// --- save ---
	private buildPayload(): string {
		// save() blocks blank titles before we get here; the fallback is so that one which somehow
		// reaches this point can never silently drop a category's icons.
		const payload = this.categories
			.map(c => ({ category_title: c.title.trim() || DEFAULT_CATEGORY_TITLE, fa_icons: c.icons.join(",") }))
			.filter(c => c.fa_icons);
		return JSON.stringify(payload);
	}

	// A 400's body is written for the admin, so prefer it. A 500's is the raw exception message,
	// which isn't fit to show anyone.
	private errorMessage(ex: any, fallback: string): string {
		const serverMessage = ex && ex.response && ex.response.status === 400 ? ex.response.data : null;
		return typeof serverMessage === "string" && serverMessage.trim() ? serverMessage : fallback;
	}

	private async save(): Promise<void> {
		// The Save button isn't rendered read-only, so this only catches a stray programmatic call.
		if (this.readOnly) {
			return;
		}
		if (this.untitledCount) {
			this.isError = true;
			this.message =
				this.untitledCount === 1
					? "One category has no name. Name it before saving."
					: `${this.untitledCount} categories have no name. Name them before saving.`;
			return;
		}
		if (this.isTooLong) {
			this.isError = true;
			this.message = "This icon set is too big to save. Remove a few icons or a whole category and try again.";
			return;
		}
		this.saving = true;
		this.message = "";
		try {
			const payload = this.buildPayload();
			// Warn before removing an icon that asset types still use — saving would reset them.
			const orphans = await organizationApi.previewAssetIconOrphans(payload);
			if (orphans && orphans.length) {
				this.pendingOrphans = orphans;
				this.pendingPayload = payload;
				this.showOrphanConfirm = true;
				this.saving = false;
				return;
			}
			await this.commitSave(payload);
		} catch (ex) {
			this.isError = true;
			this.message = this.errorMessage(ex, "Could not save the icon categories.");
			console.error("Unexpected error saving asset icon categories: " + ex);
			this.saving = false;
		}
	}

	private async confirmOrphanSave(): Promise<void> {
		await this.commitSave(this.pendingPayload);
	}

	private async commitSave(payload: string): Promise<void> {
		this.saving = true;
		try {
			await organizationApi.saveAssetIconConfig(payload);
			// Refresh the shared prefs so the Asset Type picker reflects the change immediately.
			try {
				await this.getTenantSystemPrefs(true);
			} catch (refreshEx) {
				console.error("Saved, but could not refresh org prefs: " + refreshEx);
			}
			this.isError = false;
			this.showOrphanConfirm = false;
			this.pendingOrphans = [];
			this.pendingPayload = "";
			this.$emit("hide");
		} catch (ex) {
			this.isError = true;
			this.message = this.errorMessage(ex, "Could not save the icon categories.");
			console.error("Unexpected error saving asset icon categories: " + ex);
		} finally {
			this.saving = false;
		}
	}
}
